import { Box, Typography } from '@mui/material';

import { useDateRangeLargePicker } from './hooks/useDateRangeLargePicker';
import {
  type DatePickerProps,
  DatePicker,
} from '../../mui-wrapper-components/DatePicker/DatePicker';

export type DateRangeLargePickerProps = {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id: string;

  /**
   * @prop {boolean} viewMode - if the component is in view mode both the start and end date pickers will be disabled
   * @default false
   */
  viewMode?: boolean;

  /**
   * @prop {number} width - the width of the date picker - set both the start and end date pickers
   * @default 212
   */
  width?: number;

  /**
   * @prop {DatePickerProps} startDatePicker - the start date picker properties
   */
  startDatePicker: Omit<DatePickerProps, 'viewMode' | 'id'>;

  /**
   * @prop {DatePickerProp} endDatePicker - the end date picker properties
   */
  endDatePicker: Omit<DatePickerProps, 'viewMode' | 'id'>;
};

export const DateRangePicker = (props: DateRangeLargePickerProps) => {
  const { id, viewMode = false, width = 212, startDatePicker, endDatePicker } = props;

  const {
    handleStartDateChange,
    handleEndDateChange,
    onStartDateError,
    onEndDateError,
    startHelpText,
    endHelperText,
  } = useDateRangeLargePicker(
    id,
    startDatePicker.minDate?.format('MM/DD/YYYY') || '',
    endDatePicker.maxDate?.format('MM/DD/YYYY') || '',
    startDatePicker,
    endDatePicker
  );

  return (
    <Box
      data-testid={id}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'normal',
        alignItems: 'center',
        width: '100%',
        height: viewMode ? '48px' : '76px',
        mt: '10px',
      }}
    >
      <DatePicker
        {...startDatePicker}
        id={`${id}-startDate`}
        onChange={handleStartDateChange}
        inError={startHelpText !== undefined}
        width={width}
        helperText={startHelpText || ' '}
        onError={onStartDateError}
        viewMode={viewMode}
      />
      <Typography
        variant="body1"
        sx={{
          color: 'text.primary',
          mx: '16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignSelf: 'stretch',
          height: viewMode ? '48px' : '56px',
          marginTop: viewMode ? '12px' : '0px',
          width: '10px',
        }}
      >
        —
      </Typography>
      <DatePicker
        {...endDatePicker}
        id={`${id}-endDate`}
        onChange={handleEndDateChange}
        inError={endHelperText !== undefined}
        width={width}
        helperText={endHelperText || ' '}
        onError={onEndDateError}
        viewMode={viewMode}
      />
    </Box>
  );
};
